import type { Country } from 'react-phone-number-input';
import { useNavigate } from 'react-router';

import { apiFetchUserByEmail } from '#/api/apiFetchUserByEmail';
import { useApiFetchUserLocation } from '#/api/apiFetchUserLocation';
import { apiSaveInterestFormMovement } from '#/api/apiSaveInterestFormMovement';
import { CenteredContent } from '#/components/CenteredContent/CenteredContent';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunctionData } from '#/components/Form/FormContext';
import { InputField } from '#/components/Form/InputField';
import { LocationField } from '#/components/Form/LocationField';
import { PhoneField } from '#/components/Form/PhoneField';
import { RecaptchaField } from '#/components/Form/RecaptchaField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { TextAreaField } from '#/components/Form/TextAreaField';
import { Text } from '#/components/Text/Text';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { useI18n } from '#/i18n/hooks/useI18n';
import { authSignupRoute } from '#/modules/auth/routing/auth.routes';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import {
  searchLocationToUserLocation,
  userLocationToSearchLocation,
} from '#/utils/location';
import { resetRecaptcha } from '#/utils/recaptcha';
import { scrollToTop } from '#/utils/scroll';

import { MovementPageNameFieldsHolder } from './MovementPage.styled';
import {
  type MovementPageFormValues,
  type MovementPageRedirectState,
} from './movementPageUtils';

const REDIRECT_TO_AFTER_SUCCESS = '/next';

export function MovementPage() {
  const { i18n } = useI18n();
  const { user } = useUser();
  const [userLocation] = useApiFetchUserLocation();

  useTrackPageView('Interest Form');

  const navigate = useNavigate();

  const submit = async ({
    values,
    updateSubmitting,
    handleError,
  }: FormSubmitFunctionData<MovementPageFormValues>) => {
    updateSubmitting(true);

    try {
      await apiSaveInterestFormMovement({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        aboutYourself: values.aboutYourself,
        phone: values.phone,
        linkedinUrl: values.linkedinUrl,
        gRecaptchaResponse: values.gRecaptchaResponse,
        ...searchLocationToUserLocation(values.location),
      });

      trackEvent('Success: Submitted Interest Form');

      if (
        user ||
        /* email belongs to a user */
        (await apiFetchUserByEmail(values.email))
      ) {
        scrollToTop();
        await navigate(REDIRECT_TO_AFTER_SUCCESS);
        return;
      }

      await navigate(authSignupRoute.with({ to: REDIRECT_TO_AFTER_SUCCESS }), {
        state: {
          type: 'movement-page-2nd-step',
          movementPageFormValues: values,
        } satisfies MovementPageRedirectState,
      });
    } catch (error) {
      resetRecaptcha();

      handleError(error as Error, {
        recaptchaKey: values.recaptchaKey + 1,
        gRecaptchaResponse: undefined,
      });

      updateSubmitting(false);
    }
  };

  return (
    <CenteredContent>
      <Text
        fontSize={24}
        fontWeight="semiBold"
        color="mediumContentGrey"
        textAlign="center"
        mb={32}
      >
        {i18n('Thank you for your interest!')}
      </Text>

      <Form
        analyticsTitle="Interest Form"
        usageContext="page"
        initialValues={
          {
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            email: user?.email ?? '',
            location: userLocationToSearchLocation(userLocation),
            aboutYourself: '',
            phone: '',
            linkedinUrl: '',
          } satisfies Partial<MovementPageFormValues>
        }
        qaId="movement-page-form"
        onSubmit={submit}
      >
        <MovementPageNameFieldsHolder>
          <InputField
            name="firstName"
            label={i18n('First Name') + '*'}
            placeholder={i18n('Casey')}
          />

          <InputField
            name="lastName"
            label={i18n('Last Name') + '*'}
            placeholder={i18n('Smith')}
          />
        </MovementPageNameFieldsHolder>

        <InputField
          name="email"
          type="email"
          label={i18n('Email') + '*'}
          placeholder={i18n('casey.smith@example.com')}
        />

        <LocationField
          name="location"
          label={i18n('Location') + '*'}
          customErrorMatch={(errors) => errors?.countryCode}
        />

        <TextAreaField
          name="aboutYourself"
          label={i18n(
            'Please tell us a bit about yourself, and about why this idea appeals to you:',
          )}
          maxLength={1000}
        />

        <PhoneField
          key={userLocation ? 'with-location' : 'no-user-location'}
          name="phone"
          label={i18n('Phone Number (optional)')}
          placeholder={i18n('e.g. 123 555-1234')}
          initialValueFormat="national"
          defaultCountry={userLocation?.countryCode as Country | undefined}
        />

        <InputField
          name="linkedinUrl"
          type="url"
          label={i18n('LinkedIn profile, if you have one')}
          placeholder="https://www.linkedin.com/in/amidar"
        />

        <RecaptchaField />

        <footer>
          <SubmitButton fullWidth size="large">
            {i18n('Submit')}
          </SubmitButton>
        </footer>
      </Form>
    </CenteredContent>
  );
}
